import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { detectionsTableQuery, EMPTY_DETECTION_FILTERS, type DetectionFilterValues } from '../api/securitee'
import { Content } from '../ui/Shell'
import { EdrDroppedAlert } from './EdrDroppedAlert'
import { XdrFlowAlert } from './XdrFlowAlert'
import { DetectionsFilters } from './DetectionsFilters'
import { DeviceFilterChip } from './DeviceFilterChip'
import { DataTable, useTableState } from '../ui/DataTable'
import { detectionColumns } from './columns'

/**
 * Securitee > Detections - alertele pe care Wazuh le-a intors pentru
 * evenimentele EDR ale firmei (ph4.api `docs/wazuh/README.md`). Ecran nou,
 * fara pereche in portalul vechi; in meniu numai cu dreptul
 * `securitee.detections` si EDR pornit pe firma.
 */
export const TABLE_ID = 'securiteeDetections'

/** `device`: lista vine din legatura "Its detections" (`?device=`); `onClearDevice` o scoate din adresa. */
export function SecuriteeDetectionsPage({ device = 0, onClearDevice }: { device?: number; onClearDevice?(): void } = {}) {
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 0, dir: 'desc' }, search: '' })
  const [filters, setFilters] = useState<DetectionFilterValues>(EMPTY_DETECTION_FILTERS)
  // ordonarea nu depinde de coloana "Company" (n-are ordonare), deci se alege din lista de baza
  const base = useMemo(() => detectionColumns(), [])
  const orderColumn = base.find((c) => c.index === state.order.column && c.orderable) ?? base[0]!
  const query = useQuery({
    ...detectionsTableQuery({ draw: 1, start: state.start, length: state.length, orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search }, filters, device),
    placeholderData: keepPreviousData,
  })

  // coada SOC pe "All companies": coloana "Company" (docs XDR 03, decizia 7)
  const allCompanies = query.data?.all_companies === true
  const columns = useMemo(() => detectionColumns(allCompanies), [allCompanies])
  const facets = query.data?.facets ?? { rules: [], mitre: [] }

  // un filtru nou schimba setul, deci pagina o ia de la capat
  const onFilters = (next: DetectionFilterValues) => {
    setFilters(next)
    setState({ start: 0 })
  }

  return (
    <Content>
      <div id="content">
        <XdrFlowAlert />
        <EdrDroppedAlert />
        {device > 0 && <DeviceFilterChip id={device} name={query.data?.data[0]?.device_name} onClear={onClearDevice} />}
        <DetectionsFilters filters={filters} facets={facets} onChange={onFilters} />
        <DataTable
          id={TABLE_ID}
          columns={columns}
          rows={query.data?.data ?? []}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
        />
      </div>
    </Content>
  )
}
